/**
 * Cliente de las consultas para la junta de aclaraciones (RFI).
 *
 * - `generateJunta` encola el job (202 → status 'running').
 * - `getJunta` trae el estado + resultado (los dos documentos + números).
 *
 * Los tipos son los compartidos de `@supernova/shared`: el JSON se guarda tal
 * cual lo emite el pipeline y acá se lee sin transformar.
 */
import { apiFetch } from './client.js';
import type { ProjectJunta } from '@supernova/shared';

export interface GenerateJuntaResponse {
  projectId: string;
  jobId: string | null;
  status: 'running';
}

export async function generateJunta(projectId: string): Promise<GenerateJuntaResponse> {
  return await apiFetch<GenerateJuntaResponse>(
    `/api/v1/projects/${projectId}/junta/generate`,
    { method: 'POST' },
  );
}

export async function getJunta(projectId: string): Promise<ProjectJunta> {
  return await apiFetch<ProjectJunta>(`/api/v1/projects/${projectId}/junta`);
}

/** Qué documento descargar. 'ambos' no existe en el backend: la UI baja los dos. */
export type JuntaDoc = 'escrito' | 'anexo';

/** Nombre por defecto si el header no trae uno. */
const DEFAULT_FILENAME: Record<JuntaDoc, string> = {
  escrito: 'Consultas junta.docx',
  anexo: 'Consultas de uso interno.docx',
};

/**
 * Descarga uno de los documentos de la junta en Word (.docx): el escrito de
 * consultas o el anexo técnico.
 *
 * No pasa por `apiFetch` porque la respuesta es un binario, no JSON: se pide con
 * `fetch` directo, se lee el blob y se dispara la descarga en el navegador. El
 * nombre del archivo sale del header `Content-Disposition` si viene; si no, un
 * default. Lanza `Error` con mensaje legible si el servidor no responde 200.
 */
export async function downloadJunta(projectId: string, doc: JuntaDoc): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/projects/${projectId}/junta/download?doc=${doc}`, {
      credentials: 'include',
      headers: { Accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
    });
  } catch {
    throw new Error('No se pudo descargar. Revisa tu conexión y vuelve a intentar.');
  }
  if (!res.ok) {
    let mensaje = 'No se pudo descargar el documento.';
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) mensaje = body.message;
    } catch {
      /* respuesta sin JSON: dejamos el mensaje por defecto */
    }
    throw new Error(mensaje);
  }

  const blob = await res.blob();
  const filename =
    parseFilename(res.headers.get('Content-Disposition')) ?? DEFAULT_FILENAME[doc];
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Saca el nombre de archivo del header Content-Disposition (prefiere filename* UTF-8). */
function parseFilename(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* cae al filename plano */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() ?? null;
}
